/**
 * ADR-125 — the shared tree-scoping core behind ADR-123 (Cluster tree) and
 * ADR-125 (Business Unit tree).
 *
 * This is a VIEW FILTER helper, not authorization. It decides which nodes are drawn
 * and which of those can be picked; it restricts nothing on the server.
 *
 * Three passes:
 *   1. seed              — nodes whose id is in `seedIds` (ids absent from `nodes` are ignored)
 *   2. descendant expand — plus every node beneath a seed
 *   3. ancestor closure  — plus every ancestor of (1)+(2), up to the root
 *
 * Passes 1+2 are SELECTABLE; pass 3 is CONTEXT only (drawn so the tree stays connected
 * and keeps a single root, never selectable).
 *
 * Both walks are cycle-guarded: neither `Cluster.parentClusterId` nor
 * `BusinessUnit.parentBuId` is constrained to be acyclic in the database, and an
 * infinite loop in a render path is worse than an odd-looking tree.
 *
 * Pure: no React, no network, no auth.
 *
 * @param {Array<object>} nodes flat node list
 * @param {Set<string>|null|undefined} seedIds
 * @param {(node: object) => string} getId
 * @param {(node: object) => (string|null|undefined)} getParentId
 * @returns {{ visibleIds: Set<string>|null, selectableIds: Set<string>|null, seedCount: number }}
 *          `selectableIds === null` (and `visibleIds === null`) means "no seed is present in
 *          `nodes` — do not scope". Callers render the unscoped input in that case.
 */
export const scopeTree = (nodes, seedIds, getId, getParentId) => {
  const list = Array.isArray(nodes) ? nodes : [];
  if (!seedIds || seedIds.size === 0 || list.length === 0) {
    return { visibleIds: null, selectableIds: null, seedCount: 0 };
  }

  const byId = new Map();
  const childrenOf = new Map();
  list.forEach((n) => {
    const id = getId(n);
    if (id == null) return;
    byId.set(id, n);
  });
  list.forEach((n) => {
    const id = getId(n);
    const parentId = getParentId(n);
    if (id == null || !parentId) return;
    if (!childrenOf.has(parentId)) childrenOf.set(parentId, []);
    childrenOf.get(parentId).push(id);
  });

  // Pass 1 — seeds present in this tree (stale / other-tree ids are ignored).
  const seeds = [...seedIds].filter((id) => byId.has(id));
  if (seeds.length === 0) {
    return { visibleIds: null, selectableIds: null, seedCount: 0 };
  }

  // Pass 2 — expand downwards.
  const selectableIds = new Set();
  const stack = [...seeds];
  while (stack.length) {
    const id = stack.pop();
    if (selectableIds.has(id)) continue; // cycle guard
    selectableIds.add(id);
    (childrenOf.get(id) ?? []).forEach((childId) => stack.push(childId));
  }

  // Pass 3 — close upwards so the original root survives.
  const visibleIds = new Set(selectableIds);
  selectableIds.forEach((id) => {
    let cursor = getParentId(byId.get(id));
    while (cursor && byId.has(cursor) && !visibleIds.has(cursor)) {
      visibleIds.add(cursor); // cycle guard: `has` stops a loop on its second visit
      cursor = getParentId(byId.get(cursor));
    }
  });

  return { visibleIds, selectableIds, seedCount: new Set(seeds).size };
};
